import type { Block, Blocks } from '@w/content-blocks'
import { canonicalBlock } from './reconcile-document'

export interface Token { key: string; block: Block; whitespace: boolean }

export function childrenOf(block: Block): Blocks {
  return (block as { children?: Blocks }).children ?? []
}

/** Collapse only prose whitespace; explicit br and code remain structural/literal. */
export function normalizeReviewWhitespace(block: Block, prose = false): Block {
  if (block.type === 'text') block = { ...block, text: normalizeText(block.text) }
  if (block.type === 'chars') block = { ...block, text: block.text.map(part => ({ ...part, text: normalizeText(part.text) })) }
  if (block.type === 'pre' || block.type === 'code') return { ...block, children: childrenOf(block).map(child => normalizeReviewWhitespace(child)) }
  if ((block.type === 'chars' && (block.format ?? 0) & 16) ||
    (block.type === 'text' && block.escaped)) return block
  if (prose && (block.type === 'text' || block.type === 'chars')) {
    if (block.type === 'chars' && block.format) {
      return { ...block, text: block.text.map(part => ({ ...part, text: part.text.replaceAll(/[ \t\r\n]+/gu, ' ') })) }
    }
    const { text: _text, ...properties } = block
    return { ...properties, type: 'text', text: plainText(block).replaceAll(/[ \t\r\n]+/gu, ' ') }
  }
  if (!('children' in block)) return block
  const inline = prose || ['p', 'h', 'td', 'li', 'quote'].includes(block.type)
  const children: Blocks = []
  for (const child of childrenOf(block)) {
    const next = normalizeReviewWhitespace(child, inline)
    const last = children.at(-1)
    if (inline && last?.type === 'text' && next.type === 'text' &&
      canonicalBlock({ ...last, text: '' }) === canonicalBlock({ ...next, text: '' })) {
      last.text = (last.text + next.text).replaceAll(/[ \t\r\n]+/gu, ' ')
    } else children.push(next)
  }
  return { ...block, children } as Block
}

export function plainText(block: Block): string {
  if (block.type === 'text') return block.text
  if (block.type === 'chars') return block.text.map(t => t.text).join('')
  return childrenOf(block).map(child => plainText(child)).join('')
}


const wordSegmenter = typeof Intl.Segmenter === 'function' ? new Intl.Segmenter('und', { granularity: 'word' }) : undefined
const graphemeSegmenter = typeof Intl.Segmenter === 'function' ? new Intl.Segmenter('und', { granularity: 'grapheme' }) : undefined

export function normalizeText(text: string): string {
  return text.normalize('NFC').replaceAll('\r\n', '\n')
}

/** Regex fallback retains combining marks, emoji modifiers and ZWJ sequences.
 * Without ICU word segmentation, CJK runs conservatively remain whole words. */
export function textTokens(text: string, segmenter: Intl.Segmenter | null = wordSegmenter ?? null): string[] {
  const parts = normalizeText(text).match(/[\p{L}\p{M}\p{N}_]+|\s+|\p{Regional_Indicator}{2}|\p{Extended_Pictographic}[\p{M}\p{Emoji_Modifier}]*(?:\u200D\p{Extended_Pictographic}[\p{M}\p{Emoji_Modifier}]*)*|[^\s][\p{M}]*/gu) ?? []
  return parts.flatMap(part => segmenter && /[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Thai}]/u.test(part)
    ? [...segmenter.segment(part)].map(value => value.segment) : [part])
}

export function graphemes(text: string): string[] {
  return graphemeSegmenter ? [...graphemeSegmenter.segment(text)].map(value => value.segment)
    : text.match(/\p{Regional_Indicator}{2}|\p{Extended_Pictographic}[\p{M}\p{Emoji_Modifier}]*(?:\u200D\p{Extended_Pictographic}[\p{M}\p{Emoji_Modifier}]*)*|.[\p{M}]*/gu) ?? []
}

export function textToken(text: string): Token {
  return { key: `text:${text}`, block: { id: '', type: 'text', text }, whitespace: /^\s+$/u.test(text) }
}

export function tokens(blocks: Blocks): Token[] {
  return blocks.flatMap((block): Token[] => {
    if ((block.type === 'text' || block.type === 'chars') && !('format' in block && block.format)) {
      return textTokens(plainText(block)).map(text => textToken(text))
    }
    return [{ key: canonicalBlock(block), block, whitespace: false }]
  })
}
